import type { RequiredGeneric } from '@pba/utils';
import type { EventReminder, NodeCommunity } from 'models';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { AxiosErrorHandler, Form, Skeleton } from 'ui';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useGetNodeCommunities } from 'features/communities/queries';
import {
  type ExtendedReminderInvitationsFormValues,
  InvitationLimitAlert,
  type InvitationSearchOption,
  REMINDER_ADMIN_INVITATION_LIMIT,
  ReminderInvitationsFormItem,
  useReminderInvitationCount,
} from '../../../../../../invitations-form-content';
import { useEventInviteesCount } from '../../../../../hooks';

type P = {
  event: EventReminder;
  isRequired?: boolean;
};

const ReminderInvitationsFormContentWrapper = <T extends ExtendedReminderInvitationsFormValues = never>({
  event,
  isRequired = false,
}: RequiredGeneric<T, P>) => {
  const {
    nodeUserData: { _id },
  } = useUser();
  const { canInviteCommunities } = usePermissions();
  const { t } = useTranslation();

  const {
    data: communities,
    error,
    isLoading: isLoadingCommunities,
  } = useGetNodeCommunities({
    filter: { admin: _id, ids: event.distributionList?.map((item) => item.communityId) },
    options: { enabled: canInviteCommunities({}) && (event.distributionList?.length ?? 0) > 0 },
    pagination: { pageSize: REMINDER_ADMIN_INVITATION_LIMIT },
  });

  const eventIndividualInviteesCount = useEventInviteesCount(event);

  const getEventInviteesCount = useCallback(
    (communityItems: NodeCommunity[] = []) =>
      eventIndividualInviteesCount +
      communityItems.reduce((accumulator, community) => accumulator + community.membersCount, 0),
    [eventIndividualInviteesCount],
  );

  const invitations = Form.useWatch<InvitationSearchOption[] | undefined>('invitations') ?? [];
  const { invitationCount, invitationLimit } = useReminderInvitationCount({
    alreadyInvitedCount: getEventInviteesCount(communities?.data),
    invitations,
  });

  return (
    <AxiosErrorHandler error={error}>
      {isLoadingCommunities ? (
        <Skeleton active />
      ) : (
        <>
          <ReminderInvitationsFormItem<ExtendedReminderInvitationsFormValues>
            alreadyInvitedCount={getEventInviteesCount(communities?.data)}
            isRequired={isRequired}
            label={t('Add people ({{invited}}/{{limit}})', {
              invited: invitationCount,
              limit: invitationLimit,
            })}
          />
          {invitationCount >= invitationLimit ? <InvitationLimitAlert count={invitationLimit} /> : null}
        </>
      )}
    </AxiosErrorHandler>
  );
};

export default ReminderInvitationsFormContentWrapper;
